import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

import CtaLink from '@/components/CtaLink';
import { useLanguage } from '@/components/LanguageContext';
import { withCta } from '@/lib/ctaTracking';
import { urlDe } from '@/lib/rutasPublicas';
import { pickLandingV3Content } from '@/pages/landingV3.content';

/**
 * LandingNavV3 — cabecera compartida por las diez páginas públicas del V3.
 *
 * DOS COSAS QUE EL DISEÑO NO TRAÍA, y que no son adorno:
 *
 * 1. **Menú hamburguesa.** El pill original mete seis elementos en una fila con
 *    `flex-wrap`, así que a 375 px se parte en tres filas y tapa el hero. Aquí
 *    el pill se queda en logo + hamburguesa por debajo de `md`, y todo lo demás
 *    se va a un panel desplegable.
 *
 * 2. **Los desplegables abren por hover Y por click.** En el diseño eran solo
 *    `onMouseEnter`, que en una pantalla táctil no existe: los ocho enlaces de
 *    Producto y Soluciones serían inalcanzables. En escritorio el hover sigue
 *    funcionando porque es lo que se espera de una barra así.
 *
 * En el diseño solo la home lleva los desplegables y ninguna página interior
 * tiene pie. Eso es un artefacto de que cada `.dc.html` es un fichero suelto,
 * no una decisión de producto: aquí las once montan la misma cabecera y el
 * mismo pie, como ya hacen las cinco páginas de la V2.
 *
 * La barra ámbar del teléfono que iba encima del pill se retiró a petición de
 * producto. Si vuelve, va DENTRO de este `<header>` fijo y no suelta en cada
 * página: si no, hay dos elementos `fixed` peleándose por el `top: 0`. Y hay
 * que devolverle sus 36 px a `--v3-header` en `landingV3.css`.
 */
const LOGO_SRC = '/landing-images/logo.png';

/** Los dos desplegables, en el orden en que salen en el pill. */
const GRUPOS = ['producto', 'soluciones'];

export default function LandingNavV3() {
  const { t, language } = useLanguage();
  const c = pickLandingV3Content(language);
  const { pathname } = useLocation();

  // `abierto` es el grupo desplegado en ESCRITORIO ('producto' | 'soluciones' |
  // null). Solo puede haber uno: abrir el segundo cierra el primero.
  const [abierto, setAbierto] = useState(null);
  const [menuMovil, setMenuMovil] = useState(false);
  // En el panel móvil los dos grupos son acordeones independientes, así que
  // aquí sí puede haber dos abiertos a la vez.
  const [gruposMovil, setGruposMovil] = useState({ producto: false, soluciones: false });

  const headerRef = useRef(null);
  const idPanel = useId();

  const cerrarTodo = useCallback(() => {
    setAbierto(null);
    setMenuMovil(false);
  }, []);

  // Al navegar se cierra todo. Sin esto, pulsar un enlace del panel móvil
  // cambia de página y deja el panel abierto encima del contenido nuevo.
  useEffect(() => {
    cerrarTodo();
  }, [pathname, cerrarTodo]);

  // Escape cierra lo que haya abierto, y un click fuera de la cabecera también.
  // Los dos faltaban en el nav de la V2 y se notan sobre todo con el panel
  // móvil, que ocupa media pantalla.
  useEffect(() => {
    if (!menuMovil && !abierto) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') cerrarTodo();
    };
    const onPointerDown = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) cerrarTodo();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [menuMovil, abierto, cerrarTodo]);

  return (
    <header ref={headerRef} className="fixed top-0 inset-x-0 z-50">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 mt-3 sm:mt-4 flex md:justify-center">
        <div className="flex md:inline-flex w-full md:w-auto items-center justify-between md:justify-start gap-0 md:gap-[22px] h-14 md:h-auto pl-4 pr-2 md:py-[9px] md:pr-[10px] md:pl-5 rounded-full border border-v3-line bg-white v3-shadow-pill">
          <Link to={urlDe('home', language)} className="flex items-center gap-2.5 shrink-0">
            <img src={LOGO_SRC} alt="Tapstar" className="h-[26px] w-auto block" />
          </Link>

          {/* ── Escritorio ─────────────────────────────────────────────── */}
          {GRUPOS.map((clave) => {
            const grupo = c.nav[clave];
            const estaAbierto = abierto === clave;
            return (
              <div
                key={clave}
                className="hidden md:flex relative items-center"
                onMouseEnter={() => setAbierto(clave)}
                onMouseLeave={() => setAbierto((a) => (a === clave ? null : a))}
              >
                <button
                  type="button"
                  aria-expanded={estaAbierto}
                  aria-controls={`${idPanel}-${clave}`}
                  onClick={() => setAbierto((a) => (a === clave ? null : clave))}
                  className="flex items-center gap-1.5 text-[14.5px] font-medium text-v3-body hover:text-v3-ink whitespace-nowrap transition-colors"
                >
                  {grupo.etiqueta}
                  <ChevronDown
                    aria-hidden="true"
                    className={`v3-chevron w-3 h-3 text-v3-faint ${estaAbierto ? 'rotate-180' : ''}`}
                  />
                </button>

                <div
                  id={`${idPanel}-${clave}`}
                  className={`v3-dropdown absolute top-full left-0 pt-4 ${
                    estaAbierto ? 'opacity-100' : 'opacity-0 pointer-events-none -translate-y-1.5'
                  }`}
                >
                  <div className="w-[min(560px,calc(100vw-48px))] bg-white border border-v3-line rounded-[20px] shadow-[0_18px_44px_rgba(0,0,0,.14)] p-3 text-left">
                    <div className="grid grid-cols-2 gap-0.5">
                      {grupo.items.map((it) => (
                        <Link
                          key={it.clave}
                          to={urlDe(it.clave, language)}
                          onClick={cerrarTodo}
                          className="flex gap-3 p-[13px] rounded-2xl items-start hover:bg-v3-surface transition-colors"
                        >
                          <span className="w-[34px] h-[34px] rounded-full bg-v3-goldsoft flex items-center justify-center shrink-0 text-v3-golddeep">
                            <NavIcono clave={it.clave} />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-bold tracking-[-.01em] text-v3-ink">
                              {it.titulo}
                            </span>
                            <span className="block text-[12.5px] leading-[1.45] text-v3-muted mt-[3px]">
                              {it.desc}
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Subrayado permanente, como el prototipo. Es la única entrada del
              menú que lo lleva, y ese es el punto: Multiubicaciones es la
              página del segmento que más factura y no tenía nada que la
              distinguiera de las demás. */}
          <Link
            to={urlDe('multiubicaciones', language)}
            className="hidden md:inline text-[14.5px] font-medium text-v3-body hover:text-v3-ink whitespace-nowrap underline underline-offset-4 decoration-2 decoration-v3-gold transition-colors"
          >
            {c.nav.multiubicaciones}
          </Link>

          {/* Enlace de navegación, no CTA: no empuja a registrarse, lleva a una
              página que informa. Por eso es un `<Link>` y no un `<CtaLink>`. */}
          <Link
            to={urlDe('precios', language)}
            className="hidden md:inline text-[14.5px] font-medium text-v3-body hover:text-v3-ink whitespace-nowrap transition-colors"
          >
            {c.nav.precios}
          </Link>

          <div className="hidden md:flex items-center gap-2">
            <CtaLink
              as={Link}
              cta="landing.v3.nav.login"
              to="/login"
              className="inline-flex items-center bg-white border border-v3-line text-v3-ink text-[14.5px] font-semibold rounded-full px-[17px] py-2.5 whitespace-nowrap hover:bg-v3-surface transition-colors"
            >
              {t('landing.v3.nav.login')}
            </CtaLink>
            <CtaLink
              as={Link}
              cta="landing.v3.nav.trial"
              to={withCta('/register', 'landing.v3.nav.trial')}
              className="inline-flex items-center bg-v3-gold hover:bg-v3-golddeep text-v3-ink text-[14.5px] font-semibold rounded-full px-5 py-[11px] whitespace-nowrap transition-colors"
            >
              {t('landing.v3.nav.trial')}
            </CtaLink>
          </div>

          {/* ── Móvil ──────────────────────────────────────────────────── */}
          <button
            type="button"
            onClick={() => setMenuMovil((o) => !o)}
            aria-expanded={menuMovil}
            aria-controls={`${idPanel}-movil`}
            aria-label={t(menuMovil ? 'landing.v3.nav.menuClose' : 'landing.v3.nav.menu')}
            className="md:hidden p-2 text-v3-ink"
          >
            {menuMovil ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <div
        id={`${idPanel}-movil`}
        hidden={!menuMovil}
        className="md:hidden max-w-[1180px] mx-auto px-4 sm:px-6"
      >
        {/* `max-h` + scroll: con los dos acordeones abiertos el panel mide más
            que la pantalla de un móvil pequeño, y sin esto los CTAs del final
            quedan fuera de alcance. */}
        <div className="mt-2 max-h-[calc(100vh-var(--v3-header)-16px)] overflow-y-auto rounded-3xl border border-v3-line bg-white shadow-[0_12px_40px_-12px_rgba(17,17,17,.3)] px-5 py-4 space-y-2">
          {GRUPOS.map((clave) => {
            const grupo = c.nav[clave];
            const estaAbierto = gruposMovil[clave];
            return (
              <div key={clave} className="border-b border-v3-line pb-2">
                <button
                  type="button"
                  aria-expanded={estaAbierto}
                  aria-controls={`${idPanel}-movil-${clave}`}
                  onClick={() => setGruposMovil((g) => ({ ...g, [clave]: !g[clave] }))}
                  className="w-full flex items-center justify-between py-2.5 text-[15px] font-semibold text-v3-ink"
                >
                  {grupo.etiqueta}
                  <ChevronDown
                    aria-hidden="true"
                    className={`v3-chevron w-4 h-4 text-v3-faint ${estaAbierto ? 'rotate-180' : ''}`}
                  />
                </button>
                <div id={`${idPanel}-movil-${clave}`} hidden={!estaAbierto} className="pb-1">
                  {grupo.items.map((it) => (
                    <Link
                      key={it.clave}
                      to={urlDe(it.clave, language)}
                      onClick={cerrarTodo}
                      className="flex gap-3 py-2.5 items-start"
                    >
                      <span className="w-8 h-8 rounded-full bg-v3-goldsoft flex items-center justify-center shrink-0 text-v3-golddeep">
                        <NavIcono clave={it.clave} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[14px] font-semibold text-v3-ink">
                          {it.titulo}
                        </span>
                        <span className="block text-[12.5px] leading-[1.45] text-v3-muted mt-0.5">
                          {it.desc}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}

          <Link
            to={urlDe('multiubicaciones', language)}
            onClick={cerrarTodo}
            className="block py-2.5 text-[15px] font-semibold text-v3-ink underline underline-offset-4 decoration-2 decoration-v3-gold"
          >
            {c.nav.multiubicaciones}
          </Link>

          <Link
            to={urlDe('precios', language)}
            onClick={cerrarTodo}
            className="block py-2.5 text-[15px] font-semibold text-v3-ink"
          >
            {c.nav.precios}
          </Link>

          <CtaLink
            as={Link}
            cta="landing.v3.nav.login"
            to="/login"
            onClick={cerrarTodo}
            className="inline-flex items-center justify-center w-full h-11 text-[14.5px] font-semibold rounded-full border border-v3-line text-v3-ink"
          >
            {t('landing.v3.nav.login')}
          </CtaLink>
          <CtaLink
            as={Link}
            cta="landing.v3.nav.trial"
            to={withCta('/register', 'landing.v3.nav.trial')}
            onClick={cerrarTodo}
            className="inline-flex items-center justify-center w-full h-11 text-[14.5px] font-semibold rounded-full bg-v3-gold text-v3-ink"
          >
            {t('landing.v3.nav.trial')}
          </CtaLink>
        </div>
      </div>
    </header>
  );
}

/**
 * Los iconos de los desplegables. Van aquí y no en el módulo de contenido
 * porque son marcado, no copy: traducir la página no debe poder cambiarlos, y
 * un objeto de idioma con JSX dentro no se puede mandar a traducir.
 *
 * Son los trazos del diseño, redibujados con el `stroke-width` de `lucide` para
 * que no desentonen con el resto de iconos de la página.
 */
const TRAZOS = {
  visibilidadSeo: ['M21 10c0 6-9 12-9 12S3 16 3 10a9 9 0 0 1 18 0z'],
  automatizaciones: ['M13 2L4.5 13.5H11L10 22l8.5-11.5H12z'],
  publicacionesIa: ['M4 5.5h16v15H4z', 'M4 10h16', 'M8.5 3v4', 'M15.5 3v4'],
  widgetResenas: ['M12 3.5l2.7 5.6 6.1.8-4.5 4.3 1.1 6.1L12 17.4l-5.4 2.9 1.1-6.1-4.5-4.3 6.1-.8z'],
  restauracion: [
    'M5 3v7a2.5 2.5 0 0 0 5 0V3',
    'M7.5 10v11',
    'M16.5 21v-7.5',
    'M16.5 13.5c2 0 3-1.2 3-4.5 0-3.5-1-6-3-6s-3 2.5-3 6c0 3.3 1 4.5 3 4.5z',
  ],
  clinicas: ['M5 3v5a5 5 0 0 0 10 0V3', 'M10 13v3a4 4 0 0 0 8 0v-2'],
  automotriz: [
    'M3 14.5h18',
    'M5 14.5l1.8-5a2 2 0 0 1 1.9-1.4h6.6a2 2 0 0 1 1.9 1.4l1.8 5',
    'M4 14.5v3.5h16v-3.5',
  ],
  hoteles: ['M3 19v-6.5h14a4 4 0 0 1 4 4V19', 'M3 19h18', 'M3 12.5V6', 'M6.5 12.5V10h4v2.5'],
};

const CIRCULOS = {
  visibilidadSeo: [{ cx: 12, cy: 10, r: 2.6 }],
  clinicas: [{ cx: 19, cy: 12, r: 1.6 }],
  automotriz: [
    { cx: 7.5, cy: 18, r: 1.4 },
    { cx: 16.5, cy: 18, r: 1.4 },
  ],
};

function NavIcono({ clave }) {
  const paths = TRAZOS[clave] || [];
  const circles = CIRCULOS[clave] || [];
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths.map((d) => (
        <path key={d} d={d} />
      ))}
      {circles.map((ci) => (
        <circle key={`${ci.cx}-${ci.cy}`} {...ci} />
      ))}
    </svg>
  );
}
